import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { StudioHeader } from '../components/StudioHeader';
import { SchoolSwitcher } from '../components/SchoolSwitcher';
import { ScenarioControls } from '../components/ScenarioControls';
import { ActorQuickLaunch } from '../components/ActorQuickLaunch';
import { StatsGrid } from '../components/StatsGrid';
import { LiveFeedPanel } from '../components/LiveFeedPanel';
import { useSimSchools } from '../hooks/useSimSchools';
import { useSchoolStats } from '../hooks/useSchoolStats';
import { useLiveFeed } from '../hooks/useLiveFeed';
import type { DemoSchool } from '../api/simStudioApi';
import type { Theme } from '../components/StudioHeader';

//#region AC_BLOCK sim-studio-page -- SimStudioPage raiz da rota /sim
// @ac-purpose Página principal do Simulation Studio: layout dark/light 2 colunas
export function SimStudioPage() {
  const navigate = useNavigate();

  // ── Tema persistente ──────────────────────────────────────────────────────
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('sim_studio_theme');
    return (saved as Theme) || 'dark';
  });

  const toggleTheme = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('sim_studio_theme', next);
  };

  const isDark = theme === 'dark';

  // ── Escola selecionada ────────────────────────────────────────────────────
  const { schools, loading: schoolsLoading, reload } = useSimSchools();
  const [activeSchool, setActiveSchool] = useState<DemoSchool | null>(null);

  useEffect(() => {
    if (schools.length > 0 && !activeSchool) {
      setActiveSchool(schools[0]);
    }
  }, [schools, activeSchool]);

  const { stats, loading: statsLoading } = useSchoolStats(activeSchool?.id ?? null, 10000);
  const { feed, loading: feedLoading } = useLiveFeed(activeSchool?.id ?? null, 5000);

  // ── Paleta de cores por tema ──────────────────────────────────────────────
  const colors = isDark
    ? {
        root:        'linear-gradient(160deg, #0a0a16 0%, #0f0f1e 50%, #0a0a14 100%)',
        sidebar:     'rgba(10,10,20,0.7)',
        sidebarBorder: 'rgba(255,255,255,0.05)',
        main:        'transparent',
        sectionBg:   'rgba(255,255,255,0.02)',
        sectionBorder: 'rgba(255,255,255,0.05)',
        headingColor: '#a6adc8',
        subColor:    '#4a4a6a',
        emptyColor:  '#3d3d5c',
      }
    : {
        root:        'linear-gradient(160deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
        sidebar:     'rgba(255,255,255,0.9)',
        sidebarBorder: 'rgba(0,0,0,0.06)',
        main:        'transparent',
        sectionBg:   'rgba(255,255,255,0.7)',
        sectionBorder: 'rgba(0,0,0,0.06)',
        headingColor: '#475569',
        subColor:    '#94a3b8',
        emptyColor:  '#94a3b8',
      };

  return (
    <div
      className="w-screen h-screen flex flex-col overflow-hidden select-none"
      style={{ background: colors.root }}
    >
      {/* ── Header ── */}
      <StudioHeader
        schoolName={activeSchool?.nome ?? null}
        theme={theme}
        onToggleTheme={toggleTheme}
        onNavigateDev={() => navigate('/simulador')}
      />

      {/* ── Corpo ── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* ── Sidebar (300px) ── */}
        <aside
          className="w-[300px] xl:w-[320px] flex-shrink-0 flex flex-col overflow-y-auto"
          style={{
            background: colors.sidebar,
            backdropFilter: 'blur(16px)',
            borderRight: `1px solid ${colors.sidebarBorder}`,
          }}
        >
          <div className="flex flex-col gap-5 p-4">
            <SchoolSwitcher
              schools={schools}
              activeId={activeSchool?.id ?? null}
              onSelect={setActiveSchool}
              loading={schoolsLoading}
              theme={theme}
            />

            <div style={{ borderTop: `1px solid ${colors.sidebarBorder}` }} />

            <ScenarioControls
              escolaId={activeSchool?.id ?? null}
              onSeedDone={reload}
              theme={theme}
            />

            <div style={{ borderTop: `1px solid ${colors.sidebarBorder}` }} />

            <ActorQuickLaunch
              escolaId={activeSchool?.id ?? null}
              theme={theme}
            />
          </div>
        </aside>

        {/* ── Área principal ── */}
        <main className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden">

          {/* Stats */}
          <section
            className="flex-shrink-0 px-5 py-4"
            style={{ borderBottom: `1px solid ${colors.sectionBorder}` }}
          >
            <div className="flex items-center justify-between mb-3">
              <h2
                className="text-[11px] font-bold uppercase tracking-widest"
                style={{ color: colors.headingColor }}
              >
                Resumo do Dia
                {activeSchool && (
                  <span
                    className="ml-2 font-normal normal-case tracking-normal"
                    style={{ color: colors.subColor }}
                  >
                    — {activeSchool.nome}
                  </span>
                )}
              </h2>
              {stats && (
                <span className="text-[10px]" style={{ color: colors.subColor }}>
                  Atualiza a cada 10s
                </span>
              )}
            </div>

            {!activeSchool ? (
              <p className="text-xs py-1" style={{ color: colors.emptyColor }}>
                Selecione uma escola na barra lateral.
              </p>
            ) : (
              <StatsGrid stats={stats} loading={statsLoading} theme={theme} />
            )}
          </section>

          {/* Live Feed */}
          <section className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {!activeSchool ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-8">
                <div className="text-5xl mb-4">📡</div>
                <h3
                  className="text-sm font-semibold mb-1"
                  style={{ color: colors.headingColor }}
                >
                  Nenhuma escola selecionada
                </h3>
                <p className="text-xs" style={{ color: colors.emptyColor }}>
                  Selecione uma escola na barra lateral para ver o feed ao vivo.
                </p>
              </div>
            ) : (
              <LiveFeedPanel feed={feed} loading={feedLoading} theme={theme} />
            )}
          </section>
        </main>
      </div>
    </div>
  );
}
//#endregion AC_BLOCK sim-studio-page
